import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getCategoryByName, getThreads, getLastPost, getUserDataById, getPostCountByThread } from '../../utils/api';
import { IoAddCircleOutline } from "react-icons/io5";
import { useAuth } from '../../context/AuthContext';

import Tests from '../shorts/Tests';
import News from '../shorts/News';


import '../../assets/css/forum.css';

const CategoryThreads = () => {
    const { user } = useAuth(); // Auth-Context nutzen
    const { categoryName } = useParams();
    const navigate = useNavigate();
    const [threads, setThreads] = useState([]);
    const [categoryId, setCategoryId] = useState(null);
    const [lastPosts, setLastPosts] = useState({});
    const [userData, setUserData] = useState({});
    const [postCounts, setPostCounts] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [userRole, setUserRole] = useState(null); // Benutzerrolle
    const [allowedRoles, setAllowedRoles] = useState([]); // Erlaubte Rollen

    useEffect(() => {
        const loadThreads = async () => {
            try {
                const categoryData = await getCategoryByName(categoryName);
                setCategoryId(categoryData.id);

                const data = await getThreads(1, categoryData.id);
                setThreads(data.threads);

                setUserRole(user && user.role_id ? user.role_id : null); // Setze null, wenn der Benutzer nicht angemeldet ist
                setAllowedRoles(categoryData.allowed_role_ids ? categoryData.allowed_role_ids.split(',') : []);

                const lastPostPromises = data.threads.map(async (thread) => {
                    const lastPost = await getLastPost(thread.id);
                    return { threadId: thread.id, lastPost: lastPost?.lastPost || null };
                });

                const lastPostsArray = await Promise.all(lastPostPromises);
                const lastPostsMap = lastPostsArray.reduce((acc, item) => {
                    acc[item.threadId] = item.lastPost;
                    return acc;
                }, {});

                setLastPosts(lastPostsMap);

                const userPromises = [...data.threads.map(async (thread) => {
                    const user = await getUserDataById(thread.user_id);
                    return { userId: thread.user_id, userData: user };
                }), ...lastPostsArray.map(async (post) => {
                    if (post.lastPost) {
                        const user = await getUserDataById(post.lastPost.user_id);
                        return { userId: post.lastPost.user_id, userData: user };
                    }
                    return null;
                })];

                const usersData = await Promise.all(userPromises);
                const usersMap = usersData.reduce((acc, item) => {
                    if (item) acc[item.userId] = item.userData;
                    return acc;
                }, {});

                setUserData(usersMap);

                const postCountPromises = data.threads.map(async (thread) => {
                    const postCount = await getPostCountByThread(thread.id);
                    return { threadId: thread.id, count: postCount?.postCount || 0 };
                });

                const postCountsArray = await Promise.all(postCountPromises);
                setPostCounts(postCountsArray.reduce((acc, item) => ({ ...acc, [item.threadId]: item.count }), {}));
            } catch (err) {
                setError(`Keine Themen in der Kategorie gefunden!`);
            } finally {
                setLoading(false);
            }
        };

        loadThreads();
    }, [categoryName]);

    const formatDate = (isoString) => {
        if (!isoString) return "—";
        const date = new Date(isoString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${day}.${month}.${year} um ${hours}:${minutes} Uhr`;
    };

    // Funktion, um zu überprüfen, ob der Benutzer die Berechtigung hat
    const hasCreateThreadPermission = (userRole, allowedRoles) => {
        if (userRole === null || userRole === undefined) {
            return false;  // Benutzer ist nicht angemeldet oder hat keine Rolle
        }
        return allowedRoles.some(role => parseInt(role) === userRole);
    };    

    const canCreateThread = hasCreateThreadPermission(userRole, allowedRoles);


    if (loading) {
        return <div>Lade Einträge...</div>;
    }

    return (
        <>
            <main>
                <div className='wrapper'>
                    <div className='rows'>
                        <div className='row-forum'>
                            <div className='category'>Community</div>
                            <div className='breadcrumb'>
                                <ul>
                                    <li><Link to={`/community`} viewTransition>Community</Link></li>
                                    <li>/</li>
                                    <li>{categoryName}</li>
                                </ul>
                            </div>

                            {canCreateThread && ( // Nur anzeigen, wenn der Benutzer berechtigt ist
                                <div className='create-thread-btn-container'>
                                    <Link to={`/community/create-thread?categoryId=${categoryId}&categoryName=${categoryName}`} className="create-thread-btn" viewTransition>
                                        <IoAddCircleOutline /> Thema erstellen
                                    </Link>
                                </div>
                            )}
                            {error ? (
                                <div>{error}</div>
                            ) : (
                                <>
                                    <table className='forum'>
                                        <thead>
                                            <tr>
                                                <th>Thema</th>
                                                <th>Erstellt</th>
                                                <th>Antworten</th>
                                                <th>Letzter Beitrag</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {threads.map((thread) => (
                                                <tr key={thread.id}>
                                                    <td>
                                                        <div className="category-title">
                                                            <Link to={`/community/${categoryName}/${thread.id}`} viewTransition>{thread.title}</Link>
                                                        </div>
                                                        <div className="category-description">{thread.description}</div>
                                                    </td>
                                                    <td>{formatDate(thread.created_at)}</td>
                                                    <td>{postCounts[thread.id] || 0}</td>
                                                    <td>{lastPosts[thread.id] ? formatDate(lastPosts[thread.id].created_at) : '—'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {canCreateThread && ( // Nur anzeigen, wenn der Benutzer berechtigt ist
                                        <div className='create-thread-btn-container'>
                                            <Link to={`/community/create-thread?categoryId=${categoryId}&categoryName=${categoryName}`} className="create-thread-btn" viewTransition>
                                                <IoAddCircleOutline /> Thema erstellen
                                            </Link>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                        <div className='row'>
                            <Tests />
                            <News />
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
};

export default CategoryThreads;
